import type { HostParams, RawData, TestDefinition } from './types.ts';

export interface BrowserIdentity {
  name: string;
  version: string;
}

function isRecord(value: unknown): value is RawData {
  return value != null && typeof value == 'object' && !Array.isArray(value);
}

function stableJson(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableJson).join(',')}]`;
  }

  if (value != null && typeof value == 'object') {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableJson(record[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

export function rawDataEqual(left: RawData, right: RawData): boolean {
  return stableJson(left) == stableJson(right);
}

export function testIsSpoofed(
  test: TestDefinition,
  hostParams: HostParams | null,
): boolean {
  const names = test.spoofingChromeArgs ?? [];
  const args = hostParams?.chromeArgs ?? [];
  return names.some((name) => {
    const switchName = `--${name}`;
    return args.some(
      (argument) =>
        argument == switchName || argument.startsWith(`${switchName}=`),
    );
  });
}

export function testMustMatchControl(
  test: TestDefinition,
  hostParams: HostParams | null,
): boolean {
  if (test.category != 'stableProfile' || !test.spoofingChromeArgs?.length) {
    return false;
  }
  return !testIsSpoofed(test, hostParams);
}

export function browserIdentity(rawData: RawData): BrowserIdentity {
  const userAgent = String(rawData.userAgent ?? '');
  const uaCh = isRecord(rawData.uaCh) ? rawData.uaCh : {};

  const brands = Array.isArray(uaCh.brands)
    ? uaCh.brands.filter(isRecord).map((brand) => String(brand.brand ?? ''))
    : [];

  const fullVersionList = Array.isArray(uaCh.fullVersionList)
    ? uaCh.fullVersionList.filter(isRecord)
    : [];

  const fullVersion = (...brandNames: string[]): string | null => {
    const brand = fullVersionList.find((candidate) =>
      brandNames.includes(String(candidate.brand ?? '')),
    );
    return brand && typeof brand.version == 'string' ? brand.version : null;
  };

  const patterns: Array<[string, RegExp, string[]]> = [
    ['Microsoft Edge', /(?:Edg|EdgA|EdgiOS)\/([\d.]+)/, ['Microsoft Edge']],
    ['Opera', /(?:OPR|Opera)\/([\d.]+)/, ['Opera']],
    ['Yandex Browser', /YaBrowser\/([\d.]+)/, ['Yandex Browser', 'Yandex']],
    ['Vivaldi', /Vivaldi\/([\d.]+)/, ['Vivaldi']],
    ['Firefox', /(?:Firefox|FxiOS)\/([\d.]+)/, ['Firefox']],
    ['Chromium', /Chromium\/([\d.]+)/, ['Chromium']],
  ];

  for (const [name, pattern, brandNames] of patterns) {
    const userAgentVersion = pattern.exec(userAgent)?.[1];
    if (userAgentVersion) {
      return {
        name,
        version: fullVersion(...brandNames) ?? userAgentVersion,
      };
    }
  }

  const chromeVersion = /(?:Chrome|CriOS)\/([\d.]+)/.exec(userAgent)?.[1];
  if (chromeVersion) {
    const isGoogleChrome =
      brands.includes('Google Chrome') ||
      fullVersionList.some(
        (brand) => String(brand.brand ?? '') == 'Google Chrome',
      );
    return {
      name: isGoogleChrome ? 'Google Chrome' : 'Chrome',
      version: fullVersion('Google Chrome', 'Chromium') ?? chromeVersion,
    };
  }

  const safariVersion = /Version\/([\d.]+).*Safari\//.exec(userAgent)?.[1];
  if (safariVersion) {
    return { name: 'Safari', version: safariVersion };
  }

  const tokens = [
    ...userAgent.matchAll(/\b([a-zA-Z][a-zA-Z0-9 _-]*)\/([\d.]+)/g),
  ];
  const token = tokens.at(-1);
  return token
    ? { name: token[1]?.trim() || 'Browser', version: token[2] ?? '' }
    : { name: 'Unknown browser', version: '' };
}

export function currentHostParams(): HostParams | null {
  const value = window.$DMObject?.hostParams;
  if (!isRecord(value) || !Array.isArray(value.chromeArgs)) {
    return null;
  }
  if (!value.chromeArgs.every((argument) => typeof argument == 'string')) {
    return null;
  }
  if (!isRecord(value.ips)) {
    return null;
  }
  const directIp = value.ips.directIp;
  const proxyIp = value.ips.proxyIp;
  const tcpIp = value.ips.tcpIp;
  const udpIp = value.ips.udpIp;
  if (
    typeof directIp != 'string' ||
    typeof proxyIp != 'string' ||
    typeof tcpIp != 'string' ||
    typeof udpIp != 'string'
  ) {
    return null;
  }
  return {
    chromeArgs: [...value.chromeArgs],
    ips: { directIp, proxyIp, tcpIp, udpIp },
  };
}

export function profileNameFromChromeArgs(
  chromeArgs: readonly string[] | null,
): string | null {
  if (!chromeArgs) {
    return null;
  }

  const prefix = '--user-data-dir=';
  const argument = chromeArgs.find((value) => value.startsWith(prefix));

  if (!argument) {
    return null;
  }

  const profileName = argument.match(/([^\\/]+)[\\/]?$/)?.[1];
  return profileName || null;
}
